<template>
    <div v-if="code && payload">
      <component
          :key="componentKey"
          :is="dynamicComp"
          :value="payload"
          @input="$emit('input', $event)"
          @run_action="handleRunAction"
      />
    </div>
</template>

<script>
import { fill_instance_from_schema } from "@/modules/entities/domain/GetTreeFromSchema";
import { getEditorDynamicComponent } from "@/modules/shared/infra/DynamicComponent";
import { ExecuteActionCommand } from "@/modules/entities/app/ExecuteActionCommand";
import { ApiActionExecutionRepository } from "@/modules/entities/infra/ApiActionExecutionRepository";
import { S3Repository } from '@/modules/shared/infra/S3Repository';
import awsconfig from '@/aws-exports';
import { mapState } from 'vuex';

export default {
    name: "DynamicComponentRenderer",
    provide() {
      return {
        uploadFile: this.uploadFileToS3,
        run_action_2: this.runAction2,
        getShowNext: () => this.showNext,
        getShowPrev: () => this.showPrev,
      }
    },
    data() {
      return {
        payload:null,
        componentKey: 0,
        executeActionCommand: null
      }
    },
    props: {
      value: {
        type: Object,
        required: true,
      },
      schema: {
        type: Object,
        required: true,
      },
      code : {
        type: String,
        required: true,
      },
      options: {
        type: [String, Object],
        required: false,
        default: null
      },
      actions_catalog: {
        type: Array,
        default: () => []
      },
      company_id: {
        type: [Number,String],
        required: false
      },
      // Optional dependency injection for testing
      actionExecutionRepository: {
        type: Object,
        default: null
      },
      // Delegate actions to parent instead of executing internally
      // false = execute internally (default)
      // true = delegate ALL actions to parent
      // ['action1', 'action2'] = delegate only specified actions
      delegateActions: {
        type: [Boolean, Array],
        default: false
      },
      // Navigation availability flags - passed to dynamic templates
      showNext: {
        type: Boolean,
        default: false
      },
      showPrev: {
        type: Boolean,
        default: false
      }
    },
    async mounted() {
      console.log('DynamicComponentRenderer MOUNTED')
      this.payload = fill_instance_from_schema(this.value, this.schema);
      console.log('Initialized payload:', this.payload);
      console.log(this.actions_catalog)

      // Initialize command with injected or default repository
      const repository = this.actionExecutionRepository || new ApiActionExecutionRepository();
      this.executeActionCommand = new ExecuteActionCommand(repository);
    },
    methods: {
      async handleRunAction(action_code) {
        // Check if action should be delegated to parent
        const shouldDelegate = this.delegateActions === true ||
          (Array.isArray(this.delegateActions) && this.delegateActions.includes(action_code));

        if (shouldDelegate) {
          this.$emit('run_action', action_code);
          return;
        }

        // Execute internally
        try {
          // Find action to check autoSaveAfter later
          const action = this.actions_catalog.find(a => a.code === action_code);

          const result = await this.executeActionCommand.execute({
            action_code,
            actions_catalog: this.actions_catalog,
            params: this.value,
            company_id: this.company_id
          });

          // Emit the new payload to parent
          if (result && result.result) {
            this.payload = {...result.result}; // No se pq aixo no es prou per reactivitat
            this.componentKey++; // Force re-render
            this.$emit('input', this.payload);
            this.$emit('action_completed', { action_code, result });

            // Auto-save if configured (uses 'auto_save' to avoid closing dialog)
            if (action && action.autoSaveAfter) {
              this.$emit('run_action', 'auto_save');
            }
          }
          console.log('Action executed successfully:', result);
        } catch (error) {
          console.error('Error executing action:', error);
          this.$emit('action_error', { action_code, error });
        }
      },

      async uploadFileToS3(file) {
        // Initialize AWS credentials (same pattern as GenericDocumentLoader)
        const creds = new AWS.CognitoIdentityCredentials({
          IdentityPoolId: awsconfig['aws_cognito_identity_pool_id']
        });
        creds.params.Logins = creds.params.Logins || {};
        creds.params.Logins['cognito-idp.eu-west-1.amazonaws.com/' + awsconfig['aws_user_pools_id']] =
          this.session_information.signInUserSession.idToken.jwtToken;
        creds.expired = true;

        AWS.config.update({
          region: awsconfig['aws_user_files_s3_bucket_region'],
          credentials: creds,
        });

        const s3Repo = new S3Repository(AWS, 'dynamic_uploads');
        return await s3Repo.uploadFile(file);
      },

      /**
       * Flexible action execution - always executes directly (ignores delegateActions)
       * @param {string} action_code - The action code from actions_catalog
       * @param {any} params - Params to send to the workflow
       * @returns {Promise<any>} - The result from the workflow
       */

      async runAction2(action_code, params = null) {
        const action = this.actions_catalog.find(a => a.code === action_code);
        if (!action) {
          throw new Error(`Action not found: ${action_code}`);
        }

        const repository = this.actionExecutionRepository || new ApiActionExecutionRepository();

        const response = await repository.executeAction({
          params,
          company_id: this.company_id,
          workflow_template_id: parseInt(action.workflow_id)
        });

        return response && response.result !== undefined ? response.result : response;
      }
    },
    computed:{
        ...mapState(['session_information']),
        dynamicComp(){
            return getEditorDynamicComponent(this.code, this.options);
        }
    }

  };
  </script>

  <style scoped>
  .nested-section {
    padding-left: 10px;
    /*border-left: 1px solid #ccc; */
    margin-top: 10px;
  }
  </style>

